import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSingleUserWithStats } from '../../../../services/api';
import { useParams } from 'react-router-dom';
import Breadcrumbs from '../../../../coreComponents/breadcrumbs';
import { Container, Row, Col, Card, CardBody, Input, Button, Spinner } from 'reactstrap';
import { Image } from '../../../../coreComponents/Image';
import { UserStats, UserStatsApiResponse } from '../../../../types/Dashboard';
import {
  ResponsiveContainer,
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  AreaChart,
  Area,
  Label,
  Line,
  LineChart,
} from 'recharts';
import { fmtDateTime } from '../../../../utils';
import { fmtTime, msToMin, toISODate } from '../../../../lib/dashboardUtils';
import UserDailyActivity from './UserDailyStats';
import AuthActivity from './AuthActivity';
import { CustomLoader } from '../../../uiKits/customLoader/CustomLoader';

const UserDetailsStats: React.FC = () => {
  const today = useMemo(() => new Date(), []);
  const startDefault = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d;
  }, []);
  const { userId } = useParams<{ userId: string }>();
  const [loading, setLoading] = useState<boolean>(true);
  const [user, setUser] = useState<any>(null);
  const [st, setSt] = useState<UserStats | null>(null);
  const [from, setFrom] = useState<string>(toISODate(startDefault));
  const [to, setTo] = useState<string>(toISODate(today));
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  const getUserStats = async (id: string, fromDate: string, toDate: string) => {
    let alive = true;
    try {
      if (!userId || !from || !to) return;
      const res: UserStatsApiResponse = await getSingleUserWithStats(id, fromDate, toDate);
      if (!alive) return;
      setUser(res.user);
      setSt(res.statistics);
    } catch (e) {
      console.error('getUserAndStats error', e);
    } finally {
      if (alive) setLoading(false);
      // nudge recharts after first paint
      setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
      alive = false;
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!userId || !from || !to) return;
      setLoading(true);
      try {
        const res: UserStatsApiResponse = await getSingleUserWithStats(userId, from, to);
        if (!alive) return;
        setUser(res.user);
        setSt(res.statistics);
      } catch (e) {
        console.error('getUserAndStats error', e);
      } finally {
        if (alive) setLoading(false);
        // nudge recharts after first paint
        setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Merge opens/plays per day for a combined chart
  const openPlayDaily = useMemo(() => {
    const byDate = new Map<string, { date: string; opens: number; plays: number }>();
    if (st) {
      for (const r of st.video.per_day.opens) {
        const d = r.date;
        const acc = byDate.get(d) ?? { date: d, opens: 0, plays: 0 };
        acc.opens += Number(r.count) || 0;
        byDate.set(d, acc);
      }
      for (const r of st.video.per_day.plays) {
        const d = r.date;
        const acc = byDate.get(d) ?? { date: d, opens: 0, plays: 0 };
        acc.plays += Number(r.count) || 0;
        byDate.set(d, acc);
      }
    }
    return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [st]);

  // Prepare watch per day (convert ms -> minutes for nicer scale)
  const watchDaily = useMemo(
    () =>
      (st?.video.per_day.watch_ms ?? [])
        .map((r) => ({
          date: r.date,
          watch_min: Math.round((Number(r.watch_ms) || 0) / 600) / 100,
        }))
        .sort((a, b) => a.date.localeCompare(b.date)),
    [st],
  );

  // Merge top videos (opens/plays/watch)
  const topCombined = useMemo(() => {
    const map = new Map<
      string,
      { video_id: string; title: string; opens: number; plays: number; watch_ms: number }
    >();
    const apply = (
      arr: { video_id: string; title: string; opens?: number; plays?: number; watch_ms?: number }[],
      key: 'opens' | 'plays' | 'watch_ms',
    ) => {
      for (const r of arr ?? []) {
        const id = String(r.video_id);
        const entry = map.get(id) ?? {
          video_id: id,
          title: r.title || id,
          opens: 0,
          plays: 0,
          watch_ms: 0,
        };
        if (key === 'opens') entry.opens += Number((r as any).opens) || 0;
        if (key === 'plays') entry.plays += Number((r as any).plays) || 0;
        if (key === 'watch_ms') entry.watch_ms += Number((r as any).watch_ms) || 0;
        entry.title = r.title || entry.title;
        map.set(id, entry);
      }
    };
    apply(st?.video.top.by_opens ?? [], 'opens');
    apply(st?.video.top.by_plays ?? [], 'plays');
    apply(st?.video.top.by_watch ?? [], 'watch_ms');
    return Array.from(map.values())
      .sort((a, b) => b.watch_ms - a.watch_ms)
      .slice(0, 10);
  }, [st]);

  if (!userId) return <div className="p-3">Invalid user id</div>;
  if (loading) {
    return (
      <Col xl="12" className="h-screen box-col-12 proorder-md-1">
        <Card className={`relative min-h-[400px]`}>
          <CardBody className={`premium-card flex justify-center items-center h-full`}>
            <CustomLoader />
          </CardBody>
        </Card>
      </Col>
    );
  }
  if (!st) return <div className="p-3">No stats found.</div>;

  const name =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || `User #${userId}`;

  return (
    <>
      <Breadcrumbs
        mainTitle={t('stats.userStats')}
        // parent={t('courses.generalTitle')}
      />
      <Container fluid>
        <Row>
          <Col sm="12">
            <Card>
              <Row>
                <Col sm="12">
                  {' '}
                  {/* <CommonCardHeader title={t('courses.exam.examData')} tag />{' '} */}
                </Col>
              </Row>

              <CardBody>
                <Row className="g-3 align-items-center">
                  {/* Left: avatar */}
                  <Col xs="12" md="2" className="text-center text-md-start">
                    <Image
                      src={
                        user?.photo
                          ? `${process.env.REACT_APP_BACKEND_STATIC}/userPics/${user.photo}`
                          : '/assets/images/avtar/default-user.png'
                      }
                      alt="User photo"
                      className="img-fluid rounded w-100"
                      style={{ maxWidth: 160 }} // prevents overflow on small screens
                    />
                  </Col>

                  {/* Middle: name/email */}
                  <Col xs="12" md="5">
                    <h4 className="mb-1">{t('stats.name', { name })}</h4>
                    <h6 className="mb-0 text-muted">Email: {user.email}</h6>
                  </Col>

                  {/* Right: range + controls */}
                  <Col xs="12" md="5" className="">
                    <div className="text-muted small mb-2 text-wrap d-flex justify-content-end">
                      {t('stats.range')}: {st.range.from.split('-').reverse().join('.')} →{' '}
                      {st.range.to.split('-').reverse().join('.')}
                    </div>

                    <Row className="g-2 align-items-end justify-content-end">
                      <Col xs="12" sm="auto">
                        <Label className="form-label">{t('stats.from')}</Label>
                        <Input
                          type="date"
                          value={from}
                          onChange={(e) => {
                            setFrom(e.target.value);
                          }}
                          
                        />
                      </Col>

                      <Col xs="12" sm="auto">
                        <Label className="form-label">{t('stats.to')}</Label>
                        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
                      </Col>

                      <Col xs="12" sm="auto" className="d-flex align-items-end">
                        <Button
                          color="primary"
                          onClick={() => getUserStats(userId, from, to)}
                          disabled={loading}
                        >
                          {loading ? <Spinner size="sm" /> : t('stats.refresh')}
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>

          {/* KPIs */}
          <Row className="mt-3">
            <Col md="3">
              <KPICard label={t('stats.logins')} value={st.auth.logins} />
            </Col>
            <Col md="3">
              <KPICard label={t('stats.logouts')} value={st.auth.logouts} />
            </Col>
            <Col md="3">
              <KPICard label={t('stats.lastLogin')} value={fmtDateTime(st.auth.last_login)} />
            </Col>
            <Col md="3">
              <KPICard label={t('stats.sessionsTotal')} value={st.sessions.total} />
            </Col>

            <Col md="3">
              <KPICard label={t('stats.firstSeen')} value={fmtDateTime(st.sessions.first_seen)} />
            </Col>
            <Col md="3">
              <KPICard label={t('stats.lastSeen')} value={fmtDateTime(st.sessions.last_seen)} />
            </Col>
            <Col md="3">
              <KPICard label={t('stats.videoOpensEvents')} value={st.video.opens_total} />
            </Col>
            <Col md="3">
              <KPICard label={t('stats.videoPlaysEvents')} value={st.video.plays_total} />
            </Col>
            <Col md="3">
              <KPICard
                label="Completion ≥50% (sessions)"
                value={`${st.video.completion_rate50_sessions_pct}%`}
                hint={t('stats.completion50Hint')}
              />
            </Col>
            <Col md="3">
              <KPICard
                label="Completion ≥90% (sessions)"
                value={`${st.video.completion_rate90_sessions_pct}%`}
                hint={t('stats.completion90Hint')}
              />
            </Col>

            <Col md="3">
              <KPICard
                label={t('stats.examRegistrations')}
                value={`${st.exams.registered_in_range} / ${st.exams.registered_total}`}
                hint={t('stats.examRegistrationsHint')}
              />
            </Col>
            <Col md="3">
              <KPICard
                label={t('stats.watchTimeTotal')}
                value={fmtTime(st.video.watch_total_ms, t)}
              />
            </Col>
          </Row>
          <AuthActivity auth={st.auth} title={t('stats.charts.loginsLogouts')} />
          {/* Chart: opens vs plays per day */}
          <Card className="mt-3">
            <CardBody>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>{t('stats.openVsPlayEvents')}</div>
              <div style={{ width: '100%', height: 280 }}>
                {openPlayDaily.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={openPlayDaily}
                      margin={{ left: 8, right: 16, top: 10, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis dataKey="date" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Bar
                        dataKey="opens"
                        name={t('stats.opens')}
                        fill="#9ca3af"
                        isAnimationActive={false}
                      />
                      <Bar
                        dataKey="plays"
                        name={t('stats.plays')}
                        fill="#3b82f6"
                        isAnimationActive={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ color: '#9ca3af' }}>{t('stats.noData')}</div>
                )}
              </div>
            </CardBody>
          </Card>
          {/* <Card className="mt-3">
            <CardBody>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Open → Complete (daily)</div>
              <OpenCompleteDailyChart data={st.video.open_vs_complete_daily} />
            </CardBody>
          </Card> */}
          {/* <Card className="mt-3">
            <CardBody>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Completion 50% vs 90% (daily)</div>
              <CompletionTrendChart data={st.video.open_vs_complete_daily} />
            </CardBody>
          </Card> */}
          {/* Chart: watch time per day (minutes) */}
          <Card className="mt-3">
            <CardBody>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>{t('stats.watchTimePerDay')}</div>
              <div style={{ width: '100%', height: 280 }}>
                {watchDaily.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={watchDaily}
                      margin={{ left: 8, right: 16, top: 10, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Area
                        type="monotone"
                        dataKey="watch_min"
                        name="Watch (min)"
                        fill="#93c5fd"
                        stroke="#3b82f6"
                        isAnimationActive={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ color: '#9ca3af' }}>{t('stats.noData')}</div>
                )}
              </div>
            </CardBody>
          </Card>
          {/* <Card>
            <WatchTimeDailyChart data={st.video.per_day.watch_ms} />
          </Card> */}
          {/* Top videos */}
          <Card className="mt-3">
            <CardBody>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>{t('stats.topVideosWatch')}</div>
              {topCombined.length ? (
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th style={{ minWidth: 240 }}>{t('stats.title')}</th>
                        <th className="text-end">{t('stats.opens')}</th>
                        <th className="text-end">{t('stats.plays')}</th>
                        <th className="text-end">{t('stats.watch')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topCombined.map((r) => (
                        <tr key={r.video_id}>
                          <td>{r.title}</td>
                          <td className="text-end">{r.opens}</td>
                          <td className="text-end">{r.plays}</td>
                          <td className="text-end">{fmtTime(r.watch_ms, t)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ color: '#9ca3af' }}>{t('stats.noData')}</div>
              )}
            </CardBody>
          </Card>
          <UserDailyActivity userId={userId} />
        </Row>
      </Container>
    </>
  );
};

export const KPICard: React.FC<{ label: string; value: React.ReactNode; hint?: string }> = ({
  label,
  value,
  hint,
}) => (
  <Card className="mb-3">
    <CardBody>
      <div style={{ fontSize: 12, color: '#6b7280' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700 }}>{value}</div>
      {hint ? (
        <div className="text-muted" style={{ fontSize: 12 }}>
          {hint}
        </div>
      ) : null}
    </CardBody>
  </Card>
);

export const OpenCompleteDailyChart = ({
  data,
}: {
  data: Array<{
    date: string;
    opens_sessions: number;
    completed50_sessions: number;
    completion_rate50: number;
  }>;
}) => (
  <div style={{ height: 320 }}>
    <ResponsiveContainer>
      <ComposedChart data={data} margin={{ left: 8, right: 16, top: 10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="date" />
        <YAxis yAxisId="left" allowDecimals={false} />
        <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
        <Tooltip />
        <Legend />
        <Bar yAxisId="left" dataKey="opens_sessions" name="Opens (sessions)" fill="blue" />
        <Bar
          yAxisId="left"
          dataKey="completed50_sessions"
          name="Completed ≥50% (sessions)"
          fill="red"
        />
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="completion_rate50"
          name="Completion ≥50% %"
          dot={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  </div>
);

export const CompletionTrendChart = ({
  data,
}: {
  data: Array<{
    date: string;
    completion_rate50: number;
    completion_rate90: number;
  }>;
}) => (
  <div style={{ height: 280 }}>
    <ResponsiveContainer>
      <LineChart data={data} margin={{ left: 8, right: 16, top: 10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="date" />
        <YAxis domain={[0, 100]} />
        <Tooltip />
        <Legend />
        <Line type="monotone" dataKey="completion_rate50" name="≥50% %" dot={false} />
        <Line type="monotone" dataKey="completion_rate90" name="≥90% %" dot={false} />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

export const WatchTimeDailyChart = ({
  data,
}: {
  data: Array<{ date: string; watch_ms: number }>;
}) => {
  const chart = data.map((d) => ({ date: d.date, watch_min: msToMin(d.watch_ms) }));
  return (
    <div style={{ height: 280 }}>
      <ResponsiveContainer>
        <AreaChart data={chart} margin={{ left: 8, right: 16, top: 10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
          <XAxis dataKey="date" />
          <YAxis allowDecimals />
          <Tooltip />
          <Area type="monotone" dataKey="watch_min" name="Watch (min)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};
export default UserDetailsStats;
